import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { Document, pdfjs, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { FileEarmarkText, Person, X } from 'react-bootstrap-icons';
import './pdf-viewer.scss';
import { getApiBaseUrl, isKeycloakIDP } from '../../../utils/loginProvider';
import { useReactToPrint } from 'react-to-print';
import { InvoiceDto } from '../../../models/invoice.dto';
import { useTranslation } from 'react-i18next';
import VatCodeCell from '../../invoices-list/columns/vat-code-cell';
import AccountingNumberCell from '../../invoices-list/columns/accounting-number-cell';
import CostCenter from '../../invoices-list/columns/cost-center-cell';
import AccountingRemark from '../../invoices-list/invoices-list-item/accounting-remark/accounting-remark';
import BankAccountCell from '../../invoices-list/columns/bank-account-cell';
import Description from '../../invoices-list/invoices-list-item/description/description';
import StatusDropdown from '../../invoices-list/action-dropdown/status-dropdown';
import { UserDto } from '../../../models/user.dto';
import PaymentDateField from '../../invoices-list/action-dropdown/payment-date-field';
import { notBlank } from '../../../utils/base';
import { getAccessTokenLocalStorage } from '../../../utils/token';
import { syncKeycloakAccessToken } from '../../../config/keycloak/Keycloak';
import { ReactComponent as PrintIcon } from '../../../assets/icons/print.svg';

interface Props {
  invoice: InvoiceDto
  onHide: () => void;
  show: true;
  costCentersExist: boolean;
  user?: UserDto;
}


// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//    'pdfjs-dist/build/pdf.worker.min.mjs',
//    import.meta.url
// ).toString();

pdfjs.GlobalWorkerOptions.workerSrc =
   `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ invoice, onHide, show, costCentersExist, user }: Props) => {
  const [numPages, setNumPages] = useState(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(getAccessTokenLocalStorage());
  const [mobileTab, setMobileTab] = useState<'details' | 'scan'>('details');

  const documentRef = useRef<HTMLDivElement>(null);
  const [t] = useTranslation(['account', 'common', 'invoice']);

  const receiptExists = notBlank(invoice.receiptPath);

  useEffect(() => {
    if (!isKeycloakIDP) {
      setAccessToken(getAccessTokenLocalStorage());
      return;
    }

    let cancelled = false;
    syncKeycloakAccessToken(30).then((token) => {
      if (!cancelled) {
        setAccessToken(token ?? null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [invoice.invoiceId, show]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const file = {
    url: receiptExists
       ? getApiBaseUrl() + `invoice/download/${invoice.invoiceId}/receipt`
       : getApiBaseUrl() + `invoice/download/${invoice.invoiceId}`,
    httpHeaders: {
      'authorization': accessToken ? `Bearer ${accessToken}` : ''
    }
  };

  const pages = numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : [];

  const handlePrint = useReactToPrint({
    content: () => documentRef.current,
  });

  const isPdf = (invoice: InvoiceDto): boolean => {
    if (receiptExists) {
      return invoice.receiptPath?.toLowerCase().endsWith('.pdf') ?? true;
    } else {
      return true; // non-receipt endpoint always returns pdf
    }
  };

  const isPdfFile = isPdf(invoice);

  const fileUrl = receiptExists
     ? `${getApiBaseUrl()}invoice/download/${invoice.invoiceId}/receipt`
     : `${getApiBaseUrl()}invoice/download/${invoice.invoiceId}`;

  useEffect(() => {
    if (isPdfFile) return;

    let revoked: string | null = null;
    const controller = new AbortController();

    setError(false);
    setBlobUrl(null);

    fetch(fileUrl, {
      headers: { authorization: accessToken ? `Bearer ${accessToken}` : '' },
      signal: controller.signal,
    })
       .then(res => {
         if (res.ok) {
           return res.blob();
         }
       })
       .then(blob => {
         if (blob) {
           const url = URL.createObjectURL(blob);
           revoked = url;
           setBlobUrl(url);
         } else {
           setError(true);
         }
       })
       .catch(() => setError(true));

    return () => {
      controller.abort();
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [accessToken, fileUrl, isPdfFile]);

  return (
    <Modal show={show}
      onHide={onHide}
      className='pdf-viewer'
      contentClassName='content'
    >
      <Modal.Header >
        <div className='invoice-viewer-header'>
          <div className='invoice-viewer-title'>
            <h3>{invoice.company}</h3>
            <span>{invoice.invoiceId}</span>
          </div>
          <div className='btn-container'>
            <Button className="outline-primary" onClick={handlePrint}>
              <PrintIcon className='print' />{t('common:common.print')}
            </Button>
            <Button className="outline-primary" onClick={onHide}>
              <X size={24} className='icon' />
            </Button>
          </div>
        </div>
      </Modal.Header>
      <div className='viewer-tabs' role='tablist'>
        <button type='button' className={mobileTab === 'details' ? 'active' : ''} onClick={() => setMobileTab('details')}>
          <Person /> {t('common:common.details', { defaultValue: 'Details' })}
        </button>
        <button type='button' className={mobileTab === 'scan' ? 'active' : ''} onClick={() => setMobileTab('scan')}>
          <FileEarmarkText /> {t('invoice:invoice.invoiceScan', { defaultValue: 'Invoice Scan' })}
        </button>
      </div>
      <div className='modal-container'>
        <div className={`leftform ${mobileTab !== 'details' ? 'mobile-panel-hidden' : ''}`}>
          <span className='label'>{t('invoice:invoice.bankAccount')}</span>
          <BankAccountCell invoice={invoice} />
          <span className='label'>{t('invoice:invoice.vatCode')}</span>
          <VatCodeCell invoice={invoice} />
          <span className='label'>{t('invoice:invoice.accountingNumber')}</span>
          <AccountingNumberCell invoice={invoice} />
          {costCentersExist &&
             <>
               <span className="label">{t('invoice:invoice.costCenter')}</span>
               <CostCenter invoice={invoice} />
             </>
          }
          <span className='label'>{t('invoice:invoice.accountingRemark')}</span>
          <AccountingRemark invoice={invoice} />
          <span className='label'>{t('invoice:invoice.description')}</span>
          <Description invoice={invoice} row={4} />
          <span className='label'>{t('invoice:invoice.paymentDate')}</span>
          <PaymentDateField invoice={invoice} />
          <span className='label'>{t('common:common.status.label')}</span>
          <StatusDropdown invoice={invoice} user={user} />
          <div className='viewer-mobile-actions'>
            <Button className='outline-primary' onClick={onHide}>{t('common:common.cancel')}</Button>
            <Button className='primary' onClick={onHide}>{t('common:common.save')}</Button>
          </div>
        </div>
        <div className={`invoice-document ${mobileTab !== 'scan' ? 'mobile-panel-hidden' : ''}`} ref={documentRef}>
          {isPdfFile ? (
             <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                error={<div>{t('invoice:invoice.noPdf')}</div>}
                // canvasBackground='transparent'
                loading={<Spinner className='spinner' />}
             >
               {pages.map(n => (
                  <Page
                     key={n}
                     pageNumber={n}
                     className='page'
                     width={800}
                     height={1000}
                  />
               ))}
             </Document>
          ) : (blobUrl && !error)
             ? (
               // Fallback: image formats (jpg, jpeg, png)
               <img
                  src={blobUrl}
                  alt="Invoice receipt"
                  style={{ maxWidth: '800px', maxHeight: '1000px' }}
               />
             )
             : error &&
               <div style={{ padding: 12 }}>
                  {t('invoice:invoice.noPdf')}
               </div>
          }
          <div className='viewer-mobile-actions'>
            <Button className='outline-primary' onClick={onHide}>{t('common:common.cancel')}</Button>
            <Button className='primary' onClick={handlePrint}><PrintIcon /> {t('common:common.print')}</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PdfViewer;
